import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { submitVitaminConceptTest } from './features/simulationUseCase/submitVitaminConceptTest';
import Clarity from '@microsoft/clarity';
import { useClarityTracking } from './hooks/useClarityTracking';
import Layout from './components/Layout';
import ProgressSteps from './components/ProgressSteps';
import LandingScreen from './features/landing/LandingScreen';
import type { UseCaseType } from './features/landing/LandingScreen';
import ExistingAudiences from './features/existingAudience/ExistingAudiences';
import UseCaseFormRenderer from './features/simulationUseCase/UseCaseFormRenderer';
import { markUseCaseSubmitted, startUseCase } from './features/simulationUseCase/useCaseForms/draftTeardown';
import { dropDraftImages, takeDraftImages, countDraftImages } from './features/simulationUseCase/useCaseForms/imageDraftStore';
import DraftRestoredBanner from './shared/draft/DraftRestoredBanner';
import { useDebouncedDraftSave } from './shared/draft/useDebouncedDraftSave';
import { useUnsavedWorkWarning } from './shared/draft/useUnsavedWorkWarning';
import { buildDraftIsWorthRestoring, toDraft as toBuildDraft } from './features/buildAudience/buildAudienceDraft';
import {
  simulateDraftStore, decideSimulateRestore, decideSimulateSave, draftIsWorthRestoring, describeDraft,
  type SimulateDraft, type DraftStep, type DraftDescription,
} from './features/simulationUseCase/simulateDraft';
import { isBackwardStep, visitedStepsAfterEdit } from './features/simulationUseCase/simulateSteps';
import ReviewAndRun from './features/reviewAndRun/ReviewAndRun';
import SimulationResultsContent from './features/simulationResults/SimulationResultsContent';
import PublicSimulationView from './features/simulationResults/PublicSimulationView';
import ChatPage from './features/chat/ChatPage';
import AdminPanel from './features/admin/AdminPanel';
import AnalysisPage from './features/analysis/AnalysisPage';
import ReportPage from './pages/ReportPage';
import ReportsList from './pages/ReportsList';
import { AuthProvider, useAuth } from './context/AuthContext';
import { HumanVerificationDialog } from './components/auth/HumanVerificationDialog';
import { AudienceProvider, useAudience } from './context/AudienceContext';
import { BuildAudienceProvider, useBuildAudience } from './context/BuildAudienceContext';
import { AudienceDataProvider } from './contexts/AudienceContext';
import { SubscriptionProvider, useSubscription } from './context/subscription/SubscriptionContext';
import { decideSingleSegmentSkip } from './features/simulationUseCase/singleSegmentSkip';
import { resolveSegmentNames } from './features/simulationUseCase/segmentNames';
import ErrorBoundary from './components/ErrorBoundary';
import Auth from './features/auth/Auth';
import PasswordReset from './features/auth/PasswordReset';
import ProtectedRoute from './components/ProtectedRoute';
import UseCaseRoute from './components/UseCaseRoute';
import { BLUEPILL_CHAT_USE_CASES, FOCUS_GROUP_CHAT_USE_CASES } from './config/surfaceUseCases';
import Button from './components/Button';
import { ArrowLeft } from 'lucide-react';
import AskCollectiveLanding from './features/digitalTwin/AskCollectiveLanding';
import AskCollectiveChat from './features/digitalTwin/AskCollectiveChat';
import AskHealthcareChat from './features/digitalTwin/AskHealthcareChat';
import AskNaturalOrganicChat from './features/digitalTwin/AskNaturalOrganicChat';
import ChatWithBluepill from './features/digitalTwin/ChatWithBluepill';
import SharedChatView from './features/digitalTwin/SharedChatView';
import HealthcareChat from './features/healthcareChat/HealthcareChat';
import AskRedditPersonasChat from './features/digitalTwin/AskRedditPersonasChat';
import MaintenanceMode from './components/MaintenanceMode';
import AccountDisabled from './components/AccountDisabled';
import AudienceDefine from './features/buildAudience/AudienceDefine';
import AudienceSegmentsFocus from './features/buildAudience/AudienceSegmentsFocus';
import AudienceSegmentationApproach from './features/buildAudience/AudienceSegmentationApproach';
import AudienceFinalize from './features/buildAudience/AudienceFinalize';
import PricingPage from './features/subscription/PricingPage';
import SubscriptionSuccess from './features/subscription/SubscriptionSuccess';
import SubscriptionCancelled from './features/subscription/SubscriptionCancelled';
import { checkResetAuthorization } from './services/adminService';
import SurveyBuilder from './features/humanSurvey/SurveyBuilder';
import SurveysList from './features/humanSurvey/SurveysList';
import SurveySession from './features/humanSurvey/SurveySession';
import SurveyDetails from './features/humanSurvey/SurveyDetails';
import SurveyAnalysisPage from './features/humanSurvey/SurveyAnalysisPage';
import SurveyPhotoGallery from './features/humanSurvey/SurveyPhotoGallery';
import SurveyComplete from './features/humanSurvey/SurveyComplete';
import StaticThankYou from './features/humanSurvey/StaticThankYou';
import MobilePhotoUpload from './features/humanSurvey/MobilePhotoUpload';
import SurveyResponses from './features/humanSurvey/SurveyResponses';
import SessionResponses from './features/humanSurvey/SessionResponses';
import SurveyGroupDetails from './features/humanSurvey/SurveyGroupDetails';
import SurveyChatPage from './features/humanSurvey/SurveyChatPage';
import SurveyCreatorPage from './features/surveyCreator/SurveyCreatorPage';
import SharedSurveyChatView from './features/humanSurvey/SharedSurveyChatView';
import LinkedInVerificationRedirect from './features/humanSurvey/LinkedInVerificationRedirect';
import LinkedInRejectionPage from './features/humanSurvey/LinkedInRejectionPage';
import LinkedInErrorPage from './features/humanSurvey/LinkedInErrorPage';
import UnifiedSegmentsSelector from './components/segments/UnifiedSegmentsSelector';
import { PrivacyPolicy, TermsOfService, AcceptableUsePolicy } from './features/legal';
import MarketplaceHome from './features/marketplace/MarketplaceHome';
import ArchetypeProfile from './features/marketplace/ArchetypeProfile';
import MarketplaceGate from './features/marketplace/MarketplaceGate';
import { MarketplaceAuthProvider } from './features/marketplace/MarketplaceAuthContext';
import UseCasePicker from './features/marketplace/UseCasePicker';
import FillStudyScreen from './features/marketplace/FillStudyScreen';
import ProcessingScreen from './features/marketplace/ProcessingScreen';
import PostStudyScreen from './features/marketplace/PostStudyScreen';
import BuildAudienceScreen from './features/marketplace/BuildAudienceScreen';
import ReviewScreen from './features/marketplace/ReviewScreen';
import MarketplaceResults from './features/marketplace/MarketplaceResults';
import MarketplaceHistory from './features/marketplace/MarketplaceHistory';
import ExploreTwins from './features/marketplace/ExploreTwins';
import MarketplaceFaq from './features/marketplace/MarketplaceFaq';
import MarketplaceTwinChat from './features/marketplace/MarketplaceTwinChat';
import MarketplaceNotFound from './features/marketplace/MarketplaceNotFound';
import RatingWorkbench from './features/rlhfRating/RatingWorkbench';
import LabellingWorkbench from './features/labelling/LabellingWorkbench';
import PackagingRatingPage from './features/packagingRating/PackagingRatingPage';
import ComparisonWorkbench from './features/rlhfPairwise/ComparisonWorkbench';
import DemoLogin from './features/auth/DemoLogin';

type Step =
  | 'dashboard'
  | 'use-case-form'
  | 'select-audience'
  | 'select-segments'
  | 'review-and-run'
  | 'simulation-results'
  | 'audience-define'
  | 'audience-questionnaire'
  | 'audience-segments'
  | 'audience-final';

export interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

const AppContent: React.FC = () => {
  const { autoSelectSingleSegment } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { updateAudienceData } = useAudience();
  const {
    currentStep: buildCurrentStep, setCurrentStep: setBuildCurrentStep, resetBuildAudienceData,
    restoredDraft: restoredBuildDraft, discardBuildDraft, dismissRestoredDraft,
    buildAudienceData,
  } = useBuildAudience();

  const [stepMapping, setStepMapping] = useState<Step>('dashboard');
  const [flowType, setFlowType] = useState<'build' | 'simulate' | null>(null);

  // New flow state
  const [selectedUseCaseType, setSelectedUseCaseType] = useState<UseCaseType>(null);
  // Use cases whose study has been SUBMITTED. Their draft is kept until the next study of
  // that use case starts — see draftTeardown.markUseCaseSubmitted.
  const [submittedUseCases, setSubmittedUseCases] = useState<ReadonlySet<string>>(new Set());
  const [selectedUseCaseTitle, setSelectedUseCaseTitle] = useState<string>('');
  const [savedFormPayload, setSavedFormPayload] = useState<Record<string, any> | null>(null);
  const [selectedAudienceId, setSelectedAudienceId] = useState<number | null>(null);
  const [selectedAudienceName, setSelectedAudienceName] = useState<string>('');
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<number[]>([]);
  const [selectedSegmentNames, setSelectedSegmentNames] = useState<string[]>([]);
  const [personaFilters, setPersonaFilters] = useState<Record<number, SegmentPersonaFilters>>({});
  const [dynamicSegmentationData, setDynamicSegmentationData] = useState<{
    dynamic_segmentation_id: number;
    dynamic_segment_indices: number[];
    persona_ids: number[];
    segment_names?: string[];
  } | null>(null);
  const [currentSimulationId, setCurrentSimulationId] = useState<number | null>(null);
  const [formState, setFormState] = useState<Record<string, any>>({});
  const [visitedSteps, setVisitedSteps] = useState<Set<Step>>(new Set(['dashboard']));

  // Set once when a draft is restored, so the banner can say what came back and how old
  // it is. Null means nothing was restored and no banner shows.
  const [restoredDraft, setRestoredDraft] = useState<DraftDescription | null>(null);
  const restoreAttempted = useRef(false);
  // The step a restored draft wants, handed to the route effect (which owns stepMapping
  // on /simulate) so a later replay of that effect cannot undo the restore. Consumed once.
  const pendingRestoredStep = useRef<Step | null>(null);
  // Nothing may be WRITTEN to the draft until the restore has had its chance to read it.
  // Otherwise the first render — which has default state, and a route effect that has
  // already forced stepMapping to 'dashboard' — saves over the stored draft and destroys
  // the very step the user was on.
  const [restoreSettled, setRestoreSettled] = useState(false);

  const { identifyUser, setTag } = useClarityTracking();

  useEffect(() => {
    if (isAuthenticated && user) {
      identifyUser(user.id.toString(), user.email);
      setTag('user_type', user.is_admin ? 'admin' : 'regular');
      setTag('auth_status', 'authenticated');
    } else {
      setTag('auth_status', 'unauthenticated');
    }
  }, [isAuthenticated, user, identifyUser, setTag]);

  // Mirror the wizard into the draft as it changes.
  //
  // Deliberately never writes an EMPTY draft over a real one: resetAll() fires whenever
  // the user touches '/', and a blind save at that moment would destroy the very study
  // this exists to protect. Clearing is explicit — Start fresh, or a completed run.
  useDebouncedDraftSave(() => {
    if (!restoreSettled || flowType !== 'simulate' || !user?.id) return;
    const candidate: SimulateDraft = {
      useCaseType: selectedUseCaseType,
      useCaseTitle: selectedUseCaseTitle,
      formState,
      savedFormPayload,
      audienceId: selectedAudienceId,
      audienceName: selectedAudienceName,
      segmentIds: selectedSegmentIds,
      segmentNames: selectedSegmentNames,
      personaFilters: personaFilters as unknown as Record<string, unknown>,
      dynamicSegmentationData: dynamicSegmentationData as unknown as Record<string, unknown> | null,
      step: stepMapping as DraftStep,
      visitedSteps: [...visitedSteps] as DraftStep[],
      // Counted here because this is the only place that can: the in-memory store still
      // holds the images while the form is mounted, and the count is what lets the
      // restore banner distinguish "had images" from "emitted a key on mount".
      imageCount: countDraftImages(
        (formState[(selectedUseCaseType || '').toLowerCase()] || {}).imageDraftKey as string | undefined,
      ),
    };
    // A study that has RUN must not be written back. handleSimulationComplete clears the
    // store, but it also changes formState/stepMapping/visitedSteps — all deps of this
    // effect — so without this guard the very next commit re-saved the finished study,
    // and the next session restored it into a results screen with no simulation id, i.e.
    // "We brought your study back" over "No simulation results to display".
    const decision = decideSimulateSave(candidate, submittedUseCases);
    if (decision === 'clear') { simulateDraftStore.clear(); return; }
    if (decision === 'skip') return;
    simulateDraftStore.save(candidate);
  }, [
    restoreSettled, flowType, user?.id, selectedUseCaseType, selectedUseCaseTitle, formState, savedFormPayload,
    selectedAudienceId, selectedAudienceName, selectedSegmentIds, selectedSegmentNames,
    personaFilters, dynamicSegmentationData, stepMapping, visitedSteps, submittedUseCases,
  ]);

  // The draft debounces by 400ms, so a hard close inside that window loses the last
  // keystrokes. SurveyBuilder had this guard; these two wizards did not.
  // Uses the SAME notion of "work" as the draft. Keying it on "the form emitted any key"
  // armed a browser confirm dialog on a completely untouched form, because every form
  // emits its full shape (defaults included) on mount.
  useUnsavedWorkWarning(
    flowType === 'simulate'
    && stepMapping !== 'simulation-results'
    && draftIsWorthRestoring({
      useCaseType: selectedUseCaseType,
      formState,
      savedFormPayload,
      audienceId: selectedAudienceId,
      segmentIds: selectedSegmentIds,
    } as unknown as SimulateDraft),
  );

  // The build wizard's warning lives HERE, not in BuildAudienceProvider: that provider
  // sits outside <Router> (so it cannot read the location reactively) and never unmounts,
  // so a warning armed there stayed armed on /chat, /analysis and everywhere else long
  // after the user had left the wizard.
  useUnsavedWorkWarning(
    flowType === 'build' && buildDraftIsWorthRestoring(toBuildDraft(buildAudienceData)),
  );

  /** Throw the restored study away — the banner's "Start fresh". */
  const discardRestoredDraft = () => {
    simulateDraftStore.clear();
    // Keyed, not the global clear. The store's documented invariant is that discarding
    // one form's images must not empty another form's in-progress upload — and "Start
    // fresh" discards THIS study, not every draft in the tab.
    dropDraftImages(
      (formState[(selectedUseCaseType || '').toLowerCase()] || {}).imageDraftKey as string | undefined,
    );
    setRestoredDraft(null);
    resetAll();
    navigate('/simulate');
  };

  // Build audience step mapping (unchanged)
  const updateStepMapping = useCallback((step: number) => {
    switch (step) {
      case 101: setStepMapping('audience-define'); break;
      case 102: setStepMapping('audience-questionnaire'); break;
      case 103: setStepMapping('audience-segments'); break;
      case 104: setStepMapping('audience-final'); break;
      default: setStepMapping('dashboard');
    }
  }, []);

  const handleNext = () => {
    const nextStep = buildCurrentStep + 1;
    setBuildCurrentStep(nextStep);
    updateStepMapping(nextStep);
  };

  const handleBack = () => {
    if (buildCurrentStep > 101) {
      const prevStep = buildCurrentStep - 1;
      setBuildCurrentStep(prevStep);
      updateStepMapping(prevStep);
    } else {
      setStepMapping('dashboard');
      setFlowType(null);
    }
  };

  const resetAll = () => {
    setStepMapping('dashboard');
    setBuildCurrentStep(101);
    setFlowType(null);
    setSelectedUseCaseType(null);
    setSelectedUseCaseTitle('');
    setSavedFormPayload(null);
    setSelectedAudienceId(null);
    setSelectedAudienceName('');
    setSelectedSegmentIds([]);
    setSelectedSegmentNames([]);
    setPersonaFilters({});
    setCurrentSimulationId(null);
    setFormState({});
    setVisitedSteps(new Set(['dashboard']));
    // The wizard is now empty, so a banner still saying "we brought your study back"
    // would be describing state that no longer exists.
    setRestoredDraft(null);
    updateAudienceData({
      type: null, websiteUrl: '', segmentType: null, specificSegment: '',
      qualitativeInfo: '', uploadedFile: null, audienceId: null, audienceName: '',
      selectedUseCase: null, selectedSegments: [], personaFilters: {},
    });
    resetBuildAudienceData();
  };

  // Route-based step initialization
  useEffect(() => {
    if (location.pathname === '/build-audience') {
      setFlowType('build');
      setBuildCurrentStep(101);
      updateStepMapping(101);
    } else if (location.pathname === '/simulate') {
      setFlowType('simulate');
      // Preserve progress only if already inside a Simulate-flow step;
      // otherwise (e.g. coming from a Build Audience step) land on the dashboard.
      const simulateSteps: Step[] = [
        'use-case-form', 'select-audience', 'select-segments',
        'review-and-run', 'simulation-results',
      ];
      // A restored draft names the step the user was actually on. This effect re-runs
      // (StrictMode double-invokes it, and any pathname change replays it) while the
      // restore below is deliberately once-only, so without consuming that step here the
      // second pass sends a restored mid-wizard user back to the tile picker — which is
      // the exact "my study went blank" symptom being fixed.
      if (pendingRestoredStep.current) {
        setStepMapping(pendingRestoredStep.current);
        pendingRestoredStep.current = null;
      } else if (!simulateSteps.includes(stepMapping)) {
        setStepMapping('dashboard');
      }
    } else if (location.pathname === '/') {
      // Drop any un-consumed restored step. It exists only to survive a replay of THIS
      // effect during the mount that restored it; carrying it to a later visit would jump
      // the user to Review & Run after resetAll() had emptied the payload, where Run is
      // gated only on isSubmitting and would POST an empty simulation.
      pendingRestoredStep.current = null;
      resetAll();
    }
    // There is deliberately no branch for /analysis, /chat, /admin or /subscription.
    // AppContent is only ever mounted at '/', '/build-audience' and '/simulate' (see
    // AppRoutes), so those paths cannot reach this effect. The branch that used to be
    // here implied AppContent survived those navigations, which is what made the image
    // bleed in #376 read as unreachable.
  }, [location.pathname]);

  // The build wizard's step lives in BuildAudienceContext, but the RENDERED step is
  // stepMapping here. Every call site set the two in tandem by hand, so a draft restore
  // that updated only currentStep left the stepper on the restored step while rendering
  // step 1's content (#475). Deriving one from the other removes the class of bug rather
  // than patching this instance of it.
  // Mirroring context state into local render state; there is no render-time source.
  useEffect(() => {
    if (flowType !== 'build') return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    updateStepMapping(buildCurrentStep);
  }, [flowType, buildCurrentStep, updateStepMapping]);

  // Scope the draft store as soon as we know WHO is asking — in its own effect, never as
  // a side effect of the restore.
  //
  // setScope used to live inside the restore effect, after its early returns. Any path
  // that returned early (entering the wizard by clicking a dashboard tile, the most
  // common one) therefore left the store unscoped, and every subsequent save went to the
  // bare `bp.simulate.draft.v1` key instead of `...v1.<userId>`. Two consequences: the
  // restore reads the scoped key and never finds it, so the user's work is silently
  // unrecoverable; and on a shared machine that unscoped key is exactly what the next
  // account reads — the cross-account leak the scoping exists to prevent.
  useEffect(() => {
    if (user?.id) simulateDraftStore.setScope(user.id);
  }, [user?.id]);

  // --- Draft persistence (#373, #459, #527) ---
  //
  // The wizard's whole state lived in the useState entities above and was written
  // nowhere, so a reload dropped the user on the empty tile picker with the typed study
  // gone. Restore reads localStorage once, as soon as we know WHICH user is asking.
  //
  // It has to be an effect rather than a useState initialiser because the draft key is
  // scoped by user id and `user` is still resolving on the first render — reading before
  // then would read the unscoped key and, on a shared machine, could hand one account the
  // previous account's study. Guarded by a ref so it runs exactly once per session.
  useEffect(() => {
    // Every guard lives in decideSimulateRestore so each one can be disabled by a test
    // and observed to fail; see its docstring for why that is not the default here.
    const { decision, draft } = decideSimulateRestore({
      pathname: location.pathname,
      alreadyAttempted: restoreAttempted.current,
      userId: user?.id,
      selectedUseCaseType,
      loadDraft: () => simulateDraftStore.load(),
    });

    // 'skip' must NOT mark the attempt: navigating to /simulate later still has to
    // restore, and this effect re-runs on pathname.
    if (decision === 'skip') return;

    restoreAttempted.current = true;

    // Nothing to restore, but the attempt is over — the save gate MUST open here or
    // draft writing stays disabled for the rest of the session.
    if (decision === 'settle' || !draft) { setRestoreSettled(true); return; }

    setSelectedUseCaseType(draft.useCaseType as UseCaseType);
    setSelectedUseCaseTitle(draft.useCaseTitle || '');
    setFormState(draft.formState || {});
    setSavedFormPayload(draft.savedFormPayload || null);
    setSelectedAudienceId(draft.audienceId ?? null);
    setSelectedAudienceName(draft.audienceName || '');
    setSelectedSegmentIds(draft.segmentIds || []);
    setSelectedSegmentNames(draft.segmentNames || []);
    // JSON turned the numeric segment ids into string keys; convert them back or every
    // per-segment persona filter silently misses its segment.
    setPersonaFilters(
      Object.fromEntries(
        Object.entries(draft.personaFilters || {}).map(([id, f]) => [Number(id), f]),
      ) as Record<number, SegmentPersonaFilters>,
    );
    setDynamicSegmentationData((draft.dynamicSegmentationData as typeof dynamicSegmentationData) ?? null);
    setVisitedSteps(new Set((draft.visitedSteps || ['dashboard']) as Step[]));
    setFlowType('simulate');
    setStepMapping(draft.step as Step);
    // Also hand it to the route effect: in production that effect has already run by
    // now, so the line above is what lands; under a replay it is this ref that does.
    pendingRestoredStep.current = draft.step as Step;

    const form = draft.formState?.[(draft.useCaseType || '').toLowerCase()] || {};
    setRestoredDraft(
      describeDraft(draft, Date.now(), !!takeDraftImages(form.imageDraftKey as string | undefined)),
    );
    setRestoreSettled(true);
  }, [user?.id, location.pathname, selectedUseCaseType]);

  // --- New flow handlers ---

  const handleSelectUseCase = (useCaseType: UseCaseType, useCaseTitle: string) => {
    // The user has just CHOSEN a use case, so there is nothing to restore — and the
    // restore effect has not run yet if they came from '/', because it is gated on
    // pathname. Without consuming the guard here, the navigate('/simulate') at the end of
    // this function makes the pathname change for the first time, the restore fires, and
    // it overwrites this very selection with an unrelated stale draft: click "Packaging
    // Review", get a form pre-filled from a days-old survey.
    restoreAttempted.current = true;
    // The save gate is normally opened by the restore effect, at the end of its body. By
    // consuming the guard above we make that effect return EARLY, so it never reaches
    // that line — and `restoreSettled` would stay false for the rest of the session,
    // disabling every draft write. Entering the wizard by clicking a tile is the primary
    // path, so that silently turned the whole feature off for most users.
    setRestoreSettled(true);
    // ...and the banner must not keep describing a draft the user has navigated away
    // from, or "Start fresh" would discard images belonging to the NEW use case.
    setRestoredDraft(null);
    // A study of this use case may already have run. Its inputs were kept so the results
    // screen's Edit kept working; a NEW study is starting now, so they go (#376). Computed
    // once rather than inside a setState updater, which StrictMode double-invokes.
    const started = startUseCase(formState, submittedUseCases, useCaseType);
    setFormState(started.formState);
    setSubmittedUseCases(started.submitted);
    setSelectedUseCaseType(useCaseType);
    setSelectedUseCaseTitle(useCaseTitle);
    setSavedFormPayload(null);
    setSelectedAudienceId(null);
    setSelectedAudienceName('');
    setSelectedSegmentIds([]);
    setSelectedSegmentNames([]);
    setPersonaFilters({});
    setCurrentSimulationId(null);
    setFlowType('simulate');
    setStepMapping('use-case-form');
    updateAudienceData({ selectedUseCase: useCaseTitle, selectedSegments: [], personaFilters: {} });
    setVisitedSteps(new Set(['dashboard', 'use-case-form']));
    navigate('/simulate');
  };

  const handleFormSavePayload = async (payload: Record<string, any>) => {
    setSavedFormPayload(payload);

    // Vitamin concept test handles its own submission - directly create simulation
    if (selectedUseCaseType === 'vitamin-concept-test') {
      // NOT wrapped in a try/catch, and that absence is the fix. `VitaminConceptTestForm` awaits this
      // callback inside its own try/catch and renders the message in a `role="alert"` under the Launch
      // button — but only if the rejection reaches it. This branch used to catch it, write it to
      // `console.error`, and return as though the launch had been accepted, so a customer with a spent
      // quota clicked Launch test and saw NOTHING: `createSimulation` threw `UsageLimitExceededError`
      // carrying the server's own sentence, and it died here. The form's error state was unreachable by
      // construction, which is why the fix that added it shipped and changed nothing — that fix is in the
      // deployed DEV bundle today.
      //
      // Only this branch propagates, and only the vitamin form reaches it (`selectedUseCaseType` guards it
      // and `UseCaseFormRenderer` routes that id to exactly one component), so no other use case can be
      // handed an unhandled rejection.
      await submitVitaminConceptTest(payload, {
        // Same terminal-success handling as every other use case, including the draft teardown — this path
        // used to inline the three state writes and so was the one completed study whose inputs still bled
        // into the next one (#376). Runs BEFORE the notification POST, as it always did, so the results
        // view is never held up by a courtesy request.
        onCreated: handleSimulationComplete,
        notify: user?.email
          ? async (id) => {
              await fetch(`${import.meta.env.VITE_API_URL}/monitor-simulation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ simulationId: id, email: user.email }),
              });
            }
          : undefined,
      });
      return;
    }

    if (selectedUseCaseType === 'driving-concept-test') {
      setStepMapping('review-and-run');
      setVisitedSteps(prev => new Set([...prev, 'select-audience', 'select-segments', 'review-and-run']));
      return;
    }

    setStepMapping('select-audience');
    setVisitedSteps(prev => new Set([...prev, 'select-audience']));
  };

  const handleFormDataChange = (useCaseType: string, data: Record<string, any>) => {
    const key = useCaseType.toLowerCase();
    if (JSON.stringify(formState[key]) === JSON.stringify(data)) return;
    setFormState(prev => ({ ...prev, [key]: data }));
  };

  const handleSelectAudience = async (audienceId: number, audienceName: string) => {
    setSelectedAudienceId(audienceId);
    setSelectedAudienceName(audienceName);
    updateAudienceData({
      audienceId, audienceName,
      selectedSegments: [], personaFilters: {},
    });

    // Single-segment audiences skip the segment step for plans that opt in (spec 25 / increment 05d).
    // Every summit audience is one segment, so the step rendered a single option and asked the attendee to
    // click it — a dead click in a demo someone is watching.
    //
    // Reuses `handleSegmentSelect` rather than setting the state itself: that function also stores the
    // persona filters and fetches the segment NAMES the review page reads, so an auto-selected segment
    // arrives at Review & Run indistinguishable from a hand-picked one. Reimplementing it here would
    // reproduce today's fields and silently drift from the next one added.
    if (autoSelectSingleSegment) {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/audience/${audienceId}/segments`,
          { credentials: 'include' },
        );
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data.segments || [];
          const decision = decideSingleSegmentSkip({
            autoSelectSingleSegment,
            segmentIds: list.map((seg: any) => seg.id).filter((id: any) => typeof id === 'number'),
          });
          if (decision.skip && decision.segmentId !== null) {
            // Mark the skipped step visited so the stepper renders it complete and stays clickable —
            // skipping must not mean "unreachable", or someone who wants to narrow the segment is stuck.
            setVisitedSteps(prev => new Set([...prev, 'select-segments']));
            handleSegmentSelect(
              [decision.segmentId],
              { [decision.segmentId]: { selectedPersonas: [], useAll: true } },
              undefined,
              // The id this call was given, NOT `selectedAudienceId` — that state was set at the top of this
              // function and has not been applied to this closure yet.
              audienceId,
            );
            return;
          }
        }
      } catch {
        // Any failure falls through to the normal step. Showing one extra screen is always better than
        // advancing to Review & Run with no segment selected.
      }
    }

    setStepMapping('select-segments');
    setVisitedSteps(prev => new Set([...prev, 'select-segments']));
  };

  /**
   * `audienceIdForNames` is passed in rather than read from `selectedAudienceId`, and is REQUIRED so no call
   * site can forget it. The auto-skip path calls this within the same tick as `setSelectedAudienceId`, where
   * the state has not been applied yet and this callback still closes over the previous render's `null` — so
   * the name lookup returned before it asked and Review showed "1 segment selected" for every summit
   * attendee. See features/simulationUseCase/segmentNames.ts.
   */
  const handleSegmentSelect = (
    segments: number[],
    filters: Record<number, SegmentPersonaFilters>,
    dynamicData: { dynamic_segmentation_id: number; dynamic_segment_indices: number[]; persona_ids: number[]; segment_names?: string[] } | undefined,
    audienceIdForNames: number | null,
  ) => {
    if (dynamicData) {
      // Using dynamic segmentation
      setDynamicSegmentationData(dynamicData);
      setSelectedSegmentIds([]); // Clear static segments
      setPersonaFilters({});
      // Set segment names from dynamic data
      if (dynamicData.segment_names && dynamicData.segment_names.length > 0) {
        setSelectedSegmentNames(dynamicData.segment_names);
      } else {
        setSelectedSegmentNames([`${dynamicData.dynamic_segment_indices.length} dynamic segment(s)`]);
      }
      updateAudienceData({
        selectedSegments: [],
        personaFilters: {},
        dynamicSegmentationData: dynamicData
      });
    } else {
      // Using static segments
      setDynamicSegmentationData(null);
      setSelectedSegmentIds(segments);
      setPersonaFilters(filters);
      updateAudienceData({ selectedSegments: segments, personaFilters: filters, dynamicSegmentationData: null });
      // Fetch segment names for the review page
      resolveSegmentNames(audienceIdForNames, segments).then(setSelectedSegmentNames);
    }

    setStepMapping('review-and-run');
    setVisitedSteps(prev => new Set([...prev, 'review-and-run']));
  };

  const handleSimulationComplete = (simulationId: number) => {
    setCurrentSimulationId(simulationId);
    // Run only QUEUES the study — ReviewAndRun calls this as soon as createSimulation
    // returns — so the inputs and photos are NOT done with yet. Mark the use case and let
    // the next START of it discard them (#376), because discarding here opened the
    // results screen's "Back to review" -> Edit form BLANK with the photos unrecoverable,
    // and lost the inputs of a run that failed after queueing. See draftTeardown.ts.
    setSubmittedUseCases(prev => markUseCaseSubmitted(prev, selectedUseCaseType));
    // ...and the persisted copy, or the next session would offer to "restore" a study
    // that has already run.
    simulateDraftStore.clear();
    setRestoredDraft(null);
    setStepMapping('simulation-results');
    setVisitedSteps(prev => new Set([...prev, 'simulation-results']));
  };

  const handleEditFromReview = (targetStep: Step) => {
    // Order + trimming rule live in simulateSteps so the pencil and the progress stepper
    // cannot drift apart again — each used to carry its own copy of the ladder.
    setVisitedSteps(prev => visitedStepsAfterEdit(prev, targetStep));

    if (targetStep === 'dashboard') {
      setSelectedUseCaseType(null);
      setSelectedUseCaseTitle('');
      setSavedFormPayload(null);
      setSelectedAudienceId(null);
      setSelectedAudienceName('');
      setSelectedSegmentIds([]);
      setSelectedSegmentNames([]);
      setPersonaFilters({});
    } else if (targetStep === 'use-case-form') {
      setSelectedAudienceId(null);
      setSelectedAudienceName('');
      setSelectedSegmentIds([]);
      setSelectedSegmentNames([]);
      setPersonaFilters({});
    } else if (targetStep === 'select-audience') {
      setSelectedSegmentIds([]);
      setSelectedSegmentNames([]);
      setPersonaFilters({});
    }

    setStepMapping(targetStep);
  };

  /**
   * Progress-stepper navigation. A BACKWARD jump goes through the same invalidation the
   * Review pencil uses: without it, editing a field here and stepping forward again ran a
   * paid simulation on the pre-edit payload, because savedFormPayload is only written by
   * the form's Continue (#527 P1-03). Forward moves stay plain navigation.
   */
  const goToStep = (target: Step) => {
    // The dashboard crumb is a VIEWPORT, not an edit — it shows the tile picker. Routing
    // it through handleEditFromReview would discard the use case, audience, segments and
    // saved payload, turning a "let me look at the list" click into exactly the silent
    // data loss this spec exists to fix. Only the wizard steps invalidate.
    if (target !== 'dashboard' && isBackwardStep(stepMapping, target)) handleEditFromReview(target);
    else setStepMapping(target);
  };

  // --- Progress steps ---

  const getProgressSteps = () => {
    if (flowType === 'build') {
      return [
        {
          label: '1. Define Target',
          completed: buildCurrentStep > 101,
          current: buildCurrentStep === 101,
          onClick: buildCurrentStep > 101 ? () => { setBuildCurrentStep(101); updateStepMapping(101); } : undefined,
        },
        {
          label: '2. Define Focus',
          completed: buildCurrentStep > 102,
          current: buildCurrentStep === 102,
          onClick: buildCurrentStep > 102 ? () => { setBuildCurrentStep(102); updateStepMapping(102); } : undefined,
        },
        {
          label: '3. Define Approach & Build',
          completed: buildCurrentStep > 103,
          current: buildCurrentStep === 103 || buildCurrentStep === 104,
          onClick: buildCurrentStep > 103 ? () => { setBuildCurrentStep(103); updateStepMapping(103); } : undefined,
        },
      ];
    }

    if (flowType === 'simulate') {
      const isResults = stepMapping === 'simulation-results';
      const isDriving = selectedUseCaseType === 'driving-concept-test';
      const isVitamin = selectedUseCaseType === 'vitamin-concept-test';
      const isExternalConceptTest = isDriving || isVitamin;

      if (isExternalConceptTest) {
        const stepLabel = isDriving ? '1. Driving Concept Test' : '1. Concept Test';
        return [
          {
            label: stepLabel,
            completed: visitedSteps.has('use-case-form') || isResults,
            current: stepMapping === 'dashboard',
            onClick: () => { goToStep('dashboard'); },
          },
          {
            label: '2. Configure',
            completed: visitedSteps.has('review-and-run') || isResults,
            current: stepMapping === 'use-case-form',
            onClick: visitedSteps.has('use-case-form') ? () => { goToStep('use-case-form'); } : undefined,
          },
          {
            label: '3. Review & Run',
            completed: isResults,
            current: stepMapping === 'review-and-run',
            onClick: visitedSteps.has('review-and-run') ? () => { goToStep('review-and-run'); } : undefined,
          },
        ];
      }

      return [
        {
          label: selectedUseCaseTitle ? `1. ${selectedUseCaseTitle}` : '1. Select Use Case',
          completed: visitedSteps.has('use-case-form') || isResults,
          current: stepMapping === 'dashboard',
          onClick: () => { goToStep('dashboard'); },
        },
        {
          label: '2. Complete Form',
          completed: visitedSteps.has('select-audience') || isResults,
          current: stepMapping === 'use-case-form',
          onClick: visitedSteps.has('use-case-form') ? () => { goToStep('use-case-form'); } : undefined,
        },
        {
          label: selectedAudienceName ? `3. ${selectedAudienceName}` : '3. Select Audience',
          completed: visitedSteps.has('select-segments') || isResults,
          current: stepMapping === 'select-audience',
          onClick: visitedSteps.has('select-audience') ? () => { goToStep('select-audience'); } : undefined,
        },
        {
          label: '4. Select Segments',
          completed: visitedSteps.has('review-and-run') || isResults,
          current: stepMapping === 'select-segments',
          onClick: visitedSteps.has('select-segments') ? () => { goToStep('select-segments'); } : undefined,
        },
        {
          label: '5. Review & Run',
          completed: isResults,
          current: stepMapping === 'review-and-run',
          onClick: visitedSteps.has('review-and-run') ? () => { goToStep('review-and-run'); } : undefined,
        },
      ];
    }

    return [];
  };

  // --- Render ---

  const renderCurrentStep = () => {
    switch (stepMapping) {
      case 'dashboard':
        return <LandingScreen onSelectUseCase={handleSelectUseCase} />;

      case 'use-case-form':
        return (
          <UseCaseFormRenderer
            useCaseType={selectedUseCaseType}
            onSavePayload={handleFormSavePayload}
            onBack={() => setStepMapping('dashboard')}
            onFormDataChange={handleFormDataChange}
            initialFormData={formState[(selectedUseCaseType || '').toLowerCase()] || {}}
            selectedSegmentIds={selectedSegmentIds}
            personaFilters={personaFilters}
          />
        );

      case 'select-audience':
        return (
          <ExistingAudiences
            onSelectAudience={handleSelectAudience}
            onBack={() => setStepMapping('use-case-form')}
            onCreateAudience={() => navigate('/build-audience')}
          />
        );

      case 'select-segments':
        return (
          <UnifiedSegmentsSelector
            audienceId={selectedAudienceId || 0}
            btnText="Continue to Review"
            onBack={() => setStepMapping('select-audience')}
            onNext={(segments, filters, dynamicData) =>
              handleSegmentSelect(segments, filters, dynamicData, selectedAudienceId)}
          />
        );

      case 'review-and-run':
        return (
          <ReviewAndRun
            useCaseType={selectedUseCaseType as string}
            useCaseTitle={selectedUseCaseTitle}
            audienceName={selectedAudienceName}
            audienceId={selectedAudienceId || 0}
            segmentNames={selectedSegmentNames}
            selectedSegmentIds={selectedSegmentIds}
            personaFilters={personaFilters}
            formPayload={savedFormPayload || {}}
            dynamicSegmentationData={dynamicSegmentationData}
            onEdit={handleEditFromReview}
            onComplete={handleSimulationComplete}
          />
        );

      case 'simulation-results':
        return (
          <div className="w-full" style={{ height: '90vh', overflow: 'hidden' }}>
            {currentSimulationId ? (
              <div className="h-full flex flex-col">
                <div className="mb-4 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setStepMapping('review-and-run')}
                    icon={<ArrowLeft className="w-4 h-4 mr-1" />}
                  >
                    Back to review
                  </Button>
                </div>
                <div className="flex-grow overflow-hidden">
                  <SimulationResultsContent
                    simulationId={currentSimulationId}
                    defaultChatOpen={true}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">No simulation results to display.</p>
                <Button variant="outline" className="mt-4" onClick={resetAll}>
                  Start a new study
                </Button>
              </div>
            )}
          </div>
        );

      // Build audience flow (unchanged)
      case 'audience-define':
        return <AudienceDefine onNext={handleNext} onBack={handleBack} />;
      case 'audience-questionnaire':
        return <AudienceSegmentsFocus onNext={handleNext} onBack={handleBack} />;
      case 'audience-segments':
        return <AudienceSegmentationApproach onNext={handleNext} onBack={handleBack} />;
      case 'audience-final':
        return (
          <AudienceFinalize
            // The audience now EXISTS server-side (submitted back in the approach step),
            // so the draft must go — not just the in-memory state. resetAll only calls
            // resetBuildAudienceData, which leaves localStorage intact, so a reload days
            // later restored a finished build and clicking through fired a genuine second
            // POST: a duplicate, potentially paid, audience.
            onNext={() => { discardBuildDraft(); resetAll(); }}
            onBack={handleBack}
          />
        );

      default:
        return null;
    }
  };

  if (!isAuthenticated) {
    return <Auth onAuthSuccess={() => {}} />;
  }

  // Hide progress steps for vitamin-concept-test (it has its own internal progress)
  const showProgressSteps = stepMapping !== 'dashboard' &&
    !(selectedUseCaseType === 'vitamin-concept-test' && stepMapping === 'use-case-form');

  return (
    <Layout>
      {showProgressSteps && (
        <div className="mb-4">
          <ProgressSteps steps={getProgressSteps()} />
        </div>
      )}
      {/* A restore is announced, never silent — the draft outlives the tab, so the user
          has no other way to tell whose study, and from when, is in front of them. */}
      {restoredDraft && flowType === 'simulate' && (
        <DraftRestoredBanner
          description={restoredDraft}
          onStartFresh={discardRestoredDraft}
          onDismiss={() => setRestoredDraft(null)}
        />
      )}
      {restoredBuildDraft && flowType === 'build' && (
        <DraftRestoredBanner
          description={restoredBuildDraft}
          onStartFresh={discardBuildDraft}
          onDismiss={dismissRestoredDraft}
        />
      )}
      {renderCurrentStep()}
    </Layout>
  );
};

// Authentication component with navigation
const AuthRoute: React.FC = () => {
  const { isAuthenticated, accountBlocked } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // A blocked free-tier user (failed login/verify, or already-flagged session)
  // sees the "book a call" wall instead of the login form.
  if (accountBlocked) {
    return <AccountDisabled />;
  }

  if (isAuthenticated) {
    const state = location.state as { from?: { pathname: string } };
    // Check redirect query param first, then location state, then default to home
    const redirectParam = searchParams.get('redirect');
    const from = redirectParam || state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  return <Auth onAuthSuccess={() => {}} />;
};

// Wrapper for SimulationResultsContent to use in routing
const SimulationResultsWrapper: React.FC = () => {
  const { simulationId } = useParams<{ simulationId: string }>();

  const handleError = (error: string) => {
    console.error('Simulation error:', error);
  };

  return (
    <Layout>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden h-full">
        <SimulationResultsContent
          simulationId={simulationId || ''}
          onError={handleError}
          defaultChatOpen={true}
        />
      </div>
    </Layout>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
      <Route path="/build-audience" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
      <Route path="/simulate" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
      <Route path="/simulation-results/:simulationId" element={<ProtectedRoute><SimulationResultsWrapper /></ProtectedRoute>} />
      <Route path="/analysis" element={<ProtectedRoute><AnalysisPage /></ProtectedRoute>} />
      <Route path="/rate2" element={<ProtectedRoute><ComparisonWorkbench /></ProtectedRoute>} />
      <Route path="/rate" element={<ProtectedRoute><RatingWorkbench /></ProtectedRoute>} />
      {/* Every labelling study (spec 37), under its own prefix rather than nested inside /rate.
          /rate, /rate2 and /rate-packaging are three DIFFERENT legacy surfaces; hanging the new
          engine off one of their paths implied a relationship to that one surface that does not
          exist, and made `/rate/<anything>` ambiguous with `/rate` itself. */}
      <Route path="/label/:slug" element={<ProtectedRoute><LabellingWorkbench /></ProtectedRoute>} />
      {/* Stays on its own page until the `packaging` study actually exists AND its data is
          backfilled. This previously redirected to /rate/packaging — but no study with that slug
          has ever been created, so a working route was pointed at a 404. The redirect belongs in
          the cutover commit, next to the study that makes it resolve, not ahead of it. */}
      <Route path="/rate-packaging" element={<ProtectedRoute><PackagingRatingPage /></ProtectedRoute>} />
      <Route path="/analysis/:simulationId" element={<ProtectedRoute><AnalysisPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsList /></ProtectedRoute>} />
      <Route path="/reports/:id" element={<ProtectedRoute><ReportPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/ask-collective" element={<AskCollectiveLanding />} />
      <Route path="/ask-collective/chat" element={<AskCollectiveChat />} />
      <Route path="/ask-healthcare" element={<ProtectedRoute><AskHealthcareChat /></ProtectedRoute>} />
      <Route path="/healthcare-chat" element={<ProtectedRoute><HealthcareChat /></ProtectedRoute>} />
      {/* Gated on the use case, not only on being logged in: the tiles were filtered by the backend's answer and
          the ROUTES were not, so a client with no Focus Group tile could still reach the whole chat by URL. */}
      <Route path="/ask-natural-organic/:sessionId?" element={<ProtectedRoute><UseCaseRoute useCases={FOCUS_GROUP_CHAT_USE_CASES}><AskNaturalOrganicChat /></UseCaseRoute></ProtectedRoute>} />
      <Route path="/chat-with-bluepill/:sessionId?" element={<ProtectedRoute><UseCaseRoute useCases={BLUEPILL_CHAT_USE_CASES}><ChatWithBluepill /></UseCaseRoute></ProtectedRoute>} />
      <Route path="/chat/shared/:shareToken" element={<SharedChatView />} />
      <Route path="/ask-reddit-personas" element={<ProtectedRoute><AskRedditPersonasChat /></ProtectedRoute>} />
      <Route path="/subscription/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/subscription/cancelled" element={<ProtectedRoute><SubscriptionCancelled /></ProtectedRoute>} />
      <Route path="/surveys" element={<ProtectedRoute><SurveysList /></ProtectedRoute>} />
      <Route path="/surveys/new" element={<ProtectedRoute><SurveyBuilder /></ProtectedRoute>} />
      <Route path="/surveys/:id" element={<ProtectedRoute><SurveyDetails /></ProtectedRoute>} />
      <Route path="/surveys/:shareId/take" element={<SurveySession />} />
      <Route path="/surveys/:shareId/linkedin" element={<LinkedInVerificationRedirect />} />
      <Route path="/linkedin/not-eligible" element={<LinkedInRejectionPage />} />
      <Route path="/linkedin/error" element={<LinkedInErrorPage />} />
      <Route path="/survey-groups/:groupId" element={<ProtectedRoute><SurveyGroupDetails /></ProtectedRoute>} />
      <Route path="/simulation/public/:shareToken" element={<PublicSimulationView />} />
      <Route path="/surveys/:surveyId/chat/:sessionId?" element={<ProtectedRoute><SurveyChatPage /></ProtectedRoute>} />
      <Route path="/survey-creator/:sessionId?" element={<ProtectedRoute><SurveyCreatorPage /></ProtectedRoute>} />
      <Route path="/surveys/chat/shared/:shareToken" element={<SharedSurveyChatView />} />
      <Route path="/surveys/:id/analysis" element={<ProtectedRoute><SurveyAnalysisPage /></ProtectedRoute>} />
      <Route path="/surveys/:id/images" element={<ProtectedRoute><SurveyPhotoGallery /></ProtectedRoute>} />
      <Route path="/surveys/images" element={<ProtectedRoute><SurveyPhotoGallery /></ProtectedRoute>} />
      <Route path="/surveys/:id/responses" element={<ProtectedRoute><SurveyResponses /></ProtectedRoute>} />
      <Route path="/surveys/sessions/:sessionId/responses" element={<ProtectedRoute><SessionResponses /></ProtectedRoute>} />
      {/* Session-less thank-you page. PUBLIC on purpose — a fixed, permanent URL that integrations
          without a survey session id can redirect respondents to. Distinct from the /:sessionId
          route below. */}
      <Route path="/survey-complete" element={<StaticThankYou />} />
      <Route path="/survey-complete/:sessionId" element={<SurveyComplete />} />
      <Route path="/upload/:uploadToken" element={<MobilePhotoUpload />} />
      <Route path="/auth" element={<AuthRoute />} />
      {/* The AI Summit QR code. PUBLIC on purpose — the whole point is that someone scanning a poster
          reaches it without a session, and the page's only action is asking for a sign-in link. Not wrapped
          in ProtectedRoute, and not folded into /auth, because /auth carries a password field. */}
      <Route path="/demo" element={<DemoLogin />} />
      <Route path="/reset-password" element={<PasswordReset />} />
      {/* Breakfast AI Twin Marketplace — its OWN auth (account_type='marketplace'),
          separate from the main platform AuthContext. */}
      <Route element={<MarketplaceAuthProvider><Outlet /></MarketplaceAuthProvider>}>
        <Route path="/marketplace" element={<MarketplaceHome />} />
        <Route path="/marketplace/archetype/:archetypeId" element={<ArchetypeProfile />} />
        {/* Gated study flow (MarketplaceGate → marketplace signup/login) */}
        <Route path="/marketplace/run" element={<MarketplaceGate><UseCasePicker /></MarketplaceGate>} />
        <Route path="/marketplace/run/build" element={<MarketplaceGate><BuildAudienceScreen /></MarketplaceGate>} />
        <Route path="/marketplace/run/fill" element={<MarketplaceGate><FillStudyScreen /></MarketplaceGate>} />
        <Route path="/marketplace/run/review" element={<MarketplaceGate><ReviewScreen /></MarketplaceGate>} />
        <Route path="/marketplace/run/processing" element={<MarketplaceGate><ProcessingScreen /></MarketplaceGate>} />
        <Route path="/marketplace/history" element={<MarketplaceGate><MarketplaceHistory /></MarketplaceGate>} />
        <Route path="/marketplace/twins" element={<MarketplaceGate><ExploreTwins /></MarketplaceGate>} />
        <Route path="/marketplace/faq" element={<MarketplaceGate><MarketplaceFaq /></MarketplaceGate>} />
        <Route path="/marketplace/studies/:studyId/chat" element={<MarketplaceGate><MarketplaceTwinChat /></MarketplaceGate>} />
        <Route path="/marketplace/studies/:studyId/results" element={<MarketplaceGate><MarketplaceResults /></MarketplaceGate>} />
        <Route path="/marketplace/done" element={<MarketplaceGate><PostStudyScreen /></MarketplaceGate>} />
        {/* Any unknown /marketplace/* path → a proper 404 (not a silent redirect) */}
        <Route path="/marketplace/*" element={<MarketplaceNotFound />} />
      </Route>
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/acceptable-use-policy" element={<AcceptableUsePolicy />} />
    </Routes>
  );
};

function App() {
  const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

  useEffect(() => {
    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
    const isDevelopment = import.meta.env.DEV;
    if (clarityProjectId && (!isDevelopment || import.meta.env.VITE_CLARITY_ENABLE_DEV === 'true')) {
      try {
        Clarity.init(clarityProjectId);
      } catch (error) {
        console.warn('Failed to initialize Microsoft Clarity:', error);
      }
    }
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <HumanVerificationDialog />
        <SubscriptionProvider>
          <AudienceProvider>
            <AudienceDataProvider>
              <BuildAudienceProvider>
                <Router>
                  {maintenanceMode ? <MaintenanceWrapper /> : <AppRoutes />}
                </Router>
              </BuildAudienceProvider>
            </AudienceDataProvider>
          </AudienceProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

const MaintenanceWrapper: React.FC = () => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

  useEffect(() => {
    const checkAuth = async () => {
      const authorized = await checkResetAuthorization();
      setIsAuthorized(authorized);
    };
    checkAuth();
  }, []);

  if (isAuthorized === null) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (maintenanceMode && !isAuthorized) {
    return <MaintenanceMode />;
  }

  return <AppRoutes />;
};

export default App;
